/**
 * Das Passwortfeld für den Administrator (Spec 25, Oberfläche C/D,
 * Designabnahme B5/B6) — geteilt zwischen `AccessInviteSheet`
 * (`access-invite-password`) und `AccessPersonPasswordSheet`
 * (`access-person-password-new`).
 *
 * **B5** (Designabnahme): Standardmäßig **sichtbar** — `type="text"`,
 * `autocomplete="off"`, `spellcheck="false"`. Der Administrator tippt ein
 * Passwort für jemand anderen und muss es lesen können; ein Wiederholfeld
 * hilft ihm dabei nicht (anders als im eigenen Kontoblatt, D1 — dort steht
 * `type="password"` direkt in `AccountPasswordSheet`, ohne diese Komponente).
 * `autocomplete="off"` verhindert, dass der Browser dem Administrator
 * anbietet, das Passwort einer fremden Person als eigenes zu speichern.
 *
 * **B6**: Der Umschalter trägt die immer gleiche Beschriftung „Anzeigen" und
 * meldet seinen Zustand über `aria-pressed` — kein Text, der zwischen
 * „Anzeigen"/„Verbergen" wechselt. Er blendet das Passwort bei Bedarf aus
 * (`type="password"`), Voreinstellung bleibt sichtbar.
 */

import { useId, useState } from 'react'
import type { Ref } from 'react'

import { FIELD_CLASS } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'

interface PasswordRevealFieldProps {
  readonly label: string
  readonly testId: string
  readonly apiField: string
  readonly errorPrefix: string
  readonly value: string
  readonly onChange: (value: string) => void
  readonly error: FieldError | undefined
  readonly required?: boolean
  readonly ref?: Ref<HTMLInputElement>
}

export function PasswordRevealField({
  label,
  testId,
  apiField,
  errorPrefix,
  value,
  onChange,
  error,
  required = false,
  ref,
}: PasswordRevealFieldProps) {
  // Sichtbar per Vorgabe (B5) — der Umschalter blendet bei Bedarf aus.
  const [isVisible, setIsVisible] = useState(true)
  const hasError = error?.field === apiField
  const errorTestId = `${errorPrefix}-error-${apiField}`
  const inputId = useId()

  return (
    <div className="flex flex-col gap-1">
      <label className="text-label text-muted" htmlFor={inputId}>
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          id={inputId}
          ref={ref}
          data-testid={testId}
          type={isVisible ? 'text' : 'password'}
          autoComplete="off"
          spellCheck={false}
          required={required}
          value={value}
          aria-invalid={hasError}
          aria-describedby={hasError ? errorTestId : undefined}
          onChange={(event) => {
            onChange(event.target.value)
          }}
          className={`text-text flex-1 ${FIELD_CLASS}`}
        />
        <button
          type="button"
          data-testid={`${testId}-show`}
          aria-pressed={isVisible}
          onClick={() => {
            setIsVisible((current) => !current)
          }}
          className="border-border text-accent rounded-card text-label min-h-(--tap-min) shrink-0 border px-3 transition-colors duration-(--duration-fast) ease-(--ease-out)"
        >
          Anzeigen
        </button>
      </div>
      {hasError && (
        <p id={errorTestId} data-testid={errorTestId} role="alert" className="text-status-overdue">
          {error.message}
        </p>
      )}
    </div>
  )
}
